import React from 'react';
import { Link } from 'react-router-dom';
import { Youtube, Facebook, Instagram, MapPin, Phone, Mail, Award, ShieldCheck } from 'lucide-react';
import { useCourses } from '../context/CourseContext';

export const Footer: React.FC = () => {
  const { homepageSettings } = useCourses();

  // Default values and database fallback parameters
  const footerDesc = homepageSettings?.footerDescription !== undefined 
    ? homepageSettings.footerDescription 
    : 'A modern and dynamic learning platform for students in Bangladesh, dedicated to delivering high-quality education and empowering smart learning for the future.';

  const location = homepageSettings?.footerLocation !== undefined 
    ? homepageSettings.footerLocation 
    : 'Rasulpur, Etavara, Keraniganj model, Dhaka - 1313, Bangladesh';
    
  const phone = homepageSettings?.footerPhone !== undefined 
    ? homepageSettings.footerPhone 
    : '+880 1533-381836';
    
  const email = homepageSettings?.footerEmail !== undefined 
    ? homepageSettings.footerEmail 
    : 'tazwoarbusiness@gmail.com';
    
  const tradeLicense = homepageSettings?.footerTradeLicense !== undefined 
    ? homepageSettings.footerTradeLicense 
    : '20252613817002393';

  const showLocation = homepageSettings?.showFooterLocation !== undefined ? homepageSettings.showFooterLocation : true;
  const showPhone = homepageSettings?.showFooterPhone !== undefined ? homepageSettings.showFooterPhone : true;
  const showEmail = homepageSettings?.showFooterEmail !== undefined ? homepageSettings.showFooterEmail : true;
  const showTradeLicense = homepageSettings?.showFooterTradeLicense !== undefined ? homepageSettings.showFooterTradeLicense : true;

  const youtubeUrl = homepageSettings?.footerYoutube || 'https://youtube.com';
  const facebookUrl = homepageSettings?.footerFacebook || 'https://facebook.com';
  const instagramUrl = homepageSettings?.footerInstagram || 'https://instagram.com';

  const footerYear = homepageSettings?.footerYear !== undefined 
    ? homepageSettings.footerYear 
    : new Date().getFullYear().toString();

  const footerCopyrightText = homepageSettings?.footerCopyrightText !== undefined
    ? homepageSettings.footerCopyrightText
    : 'EduCoreBD. All rights reserved.';

  const links = homepageSettings?.footerLinks !== undefined ? homepageSettings.footerLinks : [
    { title: 'Privacy Policy', url: '/privacy' },
    { title: 'Refund Policy', url: '/refund' },
    { title: 'About Us', url: '/about' }
  ];

  const showTeacherPanel = homepageSettings?.showTeacherPanelLink !== false;
  const teacherPanelTitle = homepageSettings?.teacherPanelTitle || 'শিক্ষক প্যানেল (Teacher Panel)';

  const linksList = [
    ...links,
    ...(showTeacherPanel ? [{ title: teacherPanelTitle, url: '/teacher-panel' }] : [])
  ];

  const hasContactInfo = showLocation || showPhone || showEmail || showTradeLicense;

  return (
    <footer className="relative bg-zinc-950 border-t border-zinc-900/80 pt-20 pb-12 overflow-hidden">
      {/* Soft ambient background glow for extra depth */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Brand & Mission Column */}
          <div className="md:col-span-5 space-y-6">
            <Link to="/" className="inline-block group">
              <div className="text-2xl font-bold tracking-tight text-white group-hover:text-emerald-500 transition-colors duration-300">
                EduCore<span className="text-emerald-500 group-hover:text-white transition-colors duration-300">BD</span>
              </div>
            </Link>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm font-sans">
              {footerDesc}
            </p>
            
            {/* Social handles styled like premium glass bubbles */}
            <div className="flex gap-3 pt-2">
              {youtubeUrl && (
                <a 
                  href={youtubeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 rounded-xl bg-zinc-900/80 border border-zinc-800/80 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-zinc-800 hover:border-red-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  title="YouTube"
                >
                  <Youtube size={18} />
                </a>
              )}
              {facebookUrl && (
                <a 
                  href={facebookUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 rounded-xl bg-zinc-900/80 border border-zinc-800/80 flex items-center justify-center text-zinc-400 hover:text-blue-500 hover:bg-zinc-800 hover:border-blue-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  title="Facebook"
                >
                  <Facebook size={18} />
                </a>
              )}
              {instagramUrl && (
                <a 
                  href={instagramUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 rounded-xl bg-zinc-900/80 border border-zinc-800/80 flex items-center justify-center text-zinc-400 hover:text-pink-500 hover:bg-zinc-800 hover:border-pink-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                  title="Instagram"
                >
                  <Instagram size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Quick/Important Links Column with micro-animations */}
          <div className="md:col-span-3 space-y-6">
            <h3 className="text-white font-bold text-sm uppercase tracking-wider text-zinc-200">
              Important Links
            </h3>
            {linksList.length > 0 ? (
              <ul className="space-y-4">
                {linksList.map((link, idx) => (
                  <li key={idx}>
                    {link.url.startsWith('/') ? (
                      <Link 
                        to={link.url} 
                        className="group flex items-center text-zinc-400 hover:text-emerald-400 text-sm transition-all duration-200"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2 opacity-0 group-hover:opacity-100 transition-all -translate-x-1-y group-hover:translate-x-0" />
                        {link.title}
                      </Link>
                    ) : (
                      <a 
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center text-zinc-400 hover:text-emerald-400 text-sm transition-all duration-200"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2 opacity-0 group-hover:opacity-100 transition-all -translate-x-1-y group-hover:translate-x-0" />
                        {link.title}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-zinc-600 text-xs italic">No links configured</p>
            )}
          </div>

          {/* Contact Column matching the reference image layout precisely but modernized */}
          {hasContactInfo && (
            <div className="md:col-span-4 space-y-6">
              <h3 className="text-white font-bold text-sm uppercase tracking-wider text-zinc-200">
                Contact Us
              </h3>
              <div className="space-y-4 text-sm text-zinc-400">
                {showLocation && location && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <MapPin className="text-emerald-500 animate-pulse" size={15} />
                    </div>
                    <p className="leading-relaxed font-sans text-zinc-300">
                      {location.includes(',') ? (
                        <>
                          {location.split(',')[0]}, {location.split(',')[1]}, {location.split(',')[2] || ''}<br />
                          <span className="text-zinc-400">{location.split(',').slice(3).join(',')}</span>
                        </>
                      ) : (
                        location
                      )}
                    </p>
                  </div>
                )}

                {showPhone && phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                      <Phone className="text-emerald-500" size={15} />
                    </div>
                    <a 
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      className="text-zinc-300 hover:text-emerald-400 transition-colors duration-200 font-sans"
                    >
                      {phone}
                    </a>
                  </div>
                )}

                {showEmail && email && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                      <Mail className="text-emerald-500" size={15} />
                    </div>
                    <a 
                      href={`mailto:${email}`} 
                      className="text-zinc-300 hover:text-emerald-400 transition-colors duration-200 font-sans break-all"
                    >
                      {email}
                    </a>
                  </div>
                )}
              </div>

              {/* Trade License Concept */}
              {showTradeLicense && tradeLicense && (
                <div className="pt-4 border-t border-zinc-900/60 flex items-center gap-3">
                  <div className="w-7 h-7 rounded bg-zinc-900 flex items-center justify-center text-zinc-400 border border-zinc-800">
                    <ShieldCheck size={14} className="text-emerald-500" />
                  </div>
                  <p className="text-xs text-zinc-500 font-sans">
                    <span className="font-semibold text-zinc-400">Trade License:</span> {tradeLicense}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom copyright banner */}
        <div className="mt-16 pt-8 border-t border-zinc-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-zinc-500 text-xs">
          <p className="font-sans">
            © {footerYear} <span className="text-zinc-300 font-medium">{footerCopyrightText}</span>
          </p>
          <div className="flex items-center gap-2 text-zinc-600 font-sans">
            <Award size={12} className="text-emerald-500/70" />
            <span>EduCoreBD Learner Ecosystem</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
